import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

// Състоянието на фискалния апарат, което приложението праща (aroma-backend FISCAL_DEVICE_HEALTH.md).
// Праговете за батерията са предварителни: тестов апарат стои на 7.9 V, разредените на 02.11.2026 спряха
// продажбите. Уточняват се по първите реални откази.
export const BATTERY_WARN_VOLTS = 7.2;
export const BATTERY_LOW_VOLTS = 7.0;
// Апаратът блокира продажбите, ако дълго не е предавал към НАП.
export const NAP_WARN_HOURS = 24;
export const NAP_LOW_HOURS = 48;

type ServerDate = string | number[] | null | undefined;

/** LocalDateTime от сървъра (местно време): ISO низ, а при по-стар Jackson - масив. */
export const parseServerDate = (value: ServerDate): Date | null => {
    if (!value) return null;
    if (Array.isArray(value)) {
        const [y, mo, d, h = 0, mi = 0, s = 0] = value;
        return new Date(y, mo - 1, d, h, mi, s);
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
};

const pad = (n: number) => String(n).padStart(2, '0');
export const formatShort = (date: Date) =>
    `${pad(date.getDate())}.${pad(date.getMonth() + 1)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;

export const batteryLevel = (volts: number | null | undefined): 'ok' | 'warn' | 'low' | undefined => {
    if (volts == null) return undefined;
    if (volts < BATTERY_LOW_VOLTS) return 'low';
    if (volts < BATTERY_WARN_VOLTS) return 'warn';
    return 'ok';
};

export const napLevel = (hours: number): 'ok' | 'warn' | 'low' =>
    hours > NAP_LOW_HOURS ? 'low' : hours > NAP_WARN_HOURS ? 'warn' : 'ok';

const DOT = { ok: '🟢', warn: '🟡', low: '🔴' };
const NAP_MARK = { ok: '✅', warn: '🟡', low: '🔴' };

const useAgo = () => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    return (date: Date, now = new Date()) => {
        const minutes = Math.max(0, Math.round((now.getTime() - date.getTime()) / 60000));
        const value =
            minutes < 60
                ? t('fiscalHealthMinutes', { count: minutes })
                : minutes < 48 * 60
                    ? t('fiscalHealthHours', { count: Math.round(minutes / 60) })
                    : t('fiscalHealthDays', { count: Math.round(minutes / 1440) });
        return t('fiscalHealthAgo', { value });
    };
};

export const BatteryCell = ({ volts, temperature }: { volts?: number | null; temperature?: number | null }) => {
    const level = batteryLevel(volts);
    if (!level) return <>—</>;
    return (
        <span title={temperature != null ? `${temperature}°C` : undefined}>
            {DOT[level]} {Number(volts).toFixed(1)} V
        </span>
    );
};

export const NapCell = ({ value }: { value: ServerDate }) => {
    const ago = useAgo();
    const date = parseServerDate(value);
    if (!date) return <>—</>;
    const level = napLevel((Date.now() - date.getTime()) / 3600000);
    return <span title={formatShort(date)}>{NAP_MARK[level]} {ago(date)}</span>;
};

export const LastContactCell = ({ value }: { value: ServerDate }) => {
    const date = parseServerDate(value);
    return <>{date ? formatShort(date) : '—'}</>;
};

export const RefusalCell = ({
    id,
    value,
    bits,
    volts,
    temperature,
}: {
    id?: number;
    value: ServerDate;
    bits?: string | null;
    volts?: number | null;
    temperature?: number | null;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const date = parseServerDate(value);
    if (!date) return <>—</>;
    const lines = (bits ?? '').split(';').map((line) => line.trim()).filter(Boolean);
    return (
        <OverlayTrigger
            placement='left'
            overlay={
                <Tooltip id={`fiscal-refusal-${id ?? 0}`}>
                    <div className='text-start'>
                        <div className='fw-bold'>{t('fiscalHealthRefusal')} {formatShort(date)}</div>
                        {lines.map((line) => (
                            <div key={line}>{line}</div>
                        ))}
                        {volts != null && (
                            <div>
                                {t('fiscalHealthPower')}: {Number(volts).toFixed(1)} V
                                {temperature != null ? `, ${temperature}°C` : ''}
                            </div>
                        )}
                    </div>
                </Tooltip>
            }
        >
            <span role='button'>⚠ {formatShort(date)}</span>
        </OverlayTrigger>
    );
};
